'use client';

import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { buildWebPushSubscriptionPayload, createPushSubscription, extractSubscriptionKeys, getActivePushSubscription, isPushSupported } from '@module/mdrt-org/shared/utils/helpers/web-push-subscription';
import { useUserContext } from '../../components/providers/user-context';
import { LOCAL_STORAGE_DEVICE_ID_KEY } from '../data/constants/local-storage-keys';
import { deleteWebPushSubscription } from '../data/delete-web-push-subscription';
import { postWebPushSubscription } from '../data/post-web-push-subscription';
import { AccountSettingsMutationKey } from '../enums/account-settings-mutation-key';
export const useWebPushStatus = () => {
  const {
    accessToken,
    authenticatedPersonId
  } = useUserContext();
  const [isToggleChecked, setIsToggleChecked] = useState(false);
  const [isToggleDisabled, setIsToggleDisabled] = useState(false);
  const [isHandlingToggle, setIsHandlingToggle] = useState(false);
  const [isPrePromptModalOpen, setIsPrePromptModalOpen] = useState(false);
  const {
    isPending: isPostPending,
    mutate
  } = useMutation({
    mutationFn: postWebPushSubscription,
    mutationKey: [AccountSettingsMutationKey.PATCH_PUSH_NOTIFICATION_SUBSCRIPTION],
    onError: async () => {
      setIsToggleChecked(false);
      try {
        const activeSubscription = await getActivePushSubscription();
        if (activeSubscription) await activeSubscription.unsubscribe();
      } catch {
        // best-effort browser cleanup
      }
    },
    onSuccess: () => setIsToggleChecked(true)
  });
  const {
    isPending: isDeletePending,
    mutateAsync: deleteSubscriptionAsync
  } = useMutation({
    mutationFn: deleteWebPushSubscription,
    mutationKey: [AccountSettingsMutationKey.DELETE_PUSH_NOTIFICATION_SUBSCRIPTION]
  });
  useEffect(() => {
    const checkPushStatus = async () => {
      if (!isPushSupported()) return;
      const permission = Notification.permission;
      if (permission === 'denied') {
        setIsToggleChecked(false);
        setIsToggleDisabled(true);
        try {
          const activeSubscription_0 = await getActivePushSubscription();
          if (activeSubscription_0) {
            const {
              auth,
              endpoint,
              p256dh
            } = extractSubscriptionKeys(activeSubscription_0);
            const deviceId = localStorage.getItem(LOCAL_STORAGE_DEVICE_ID_KEY) ?? undefined;
            await deleteSubscriptionAsync({
              accessToken,
              auth,
              deviceId,
              endpoint,
              p256dh,
              personId: authenticatedPersonId
            });
            await activeSubscription_0.unsubscribe();
          }
        } catch {
          // best-effort cleanup — ignore failures
        }
        return;
      }
      if (permission === 'default') {
        setIsToggleChecked(false);
        setIsToggleDisabled(false);
        return;
      }
      try {
        const activeSubscription_1 = await getActivePushSubscription();
        setIsToggleChecked(activeSubscription_1 !== null);
        setIsToggleDisabled(false);
      } catch {
        setIsToggleChecked(false);
        setIsToggleDisabled(false);
      }
    };
    checkPushStatus();
  }, [accessToken, authenticatedPersonId, deleteSubscriptionAsync]);
  const handlePushToggle = async () => {
    if (!isPushSupported() || isHandlingToggle) return;
    setIsHandlingToggle(true);
    try {
      if (isToggleChecked) {
        const activeSubscription_2 = await getActivePushSubscription();
        if (activeSubscription_2) {
          const {
            auth: deleteAuth,
            endpoint: deleteEndpoint,
            p256dh: deleteP256dh
          } = extractSubscriptionKeys(activeSubscription_2);
          const deleteDeviceId = localStorage.getItem(LOCAL_STORAGE_DEVICE_ID_KEY) ?? undefined;
          try {
            await deleteSubscriptionAsync({
              accessToken,
              auth: deleteAuth,
              deviceId: deleteDeviceId,
              endpoint: deleteEndpoint,
              p256dh: deleteP256dh,
              personId: authenticatedPersonId
            });
            setIsToggleChecked(false);
            try {
              await activeSubscription_2.unsubscribe();
            } catch {
              // best-effort — backend already cleaned up
            }
          } catch {
            // browser subscription still active — toggle remains ON
          }
        }
        return;
      }
      if (Notification.permission === 'default') {
        setIsPrePromptModalOpen(true);
        return;
      }
      const newSubscription = await createPushSubscription();
      if (!newSubscription) return;
      mutate(buildWebPushSubscriptionPayload(newSubscription, {
        accessToken,
        personId: authenticatedPersonId
      }));
    } catch {
      // push setup failed (e.g. service worker unavailable) — leave toggle OFF
      setIsToggleChecked(false);
    } finally {
      setIsHandlingToggle(false);
    }
  };
  const handlePrePromptAllow = async () => {
    setIsHandlingToggle(true);
    try {
      const newSubscription_0 = await createPushSubscription();
      if (!newSubscription_0) {
        if (Notification.permission === 'denied') {
          setIsToggleChecked(false);
          setIsToggleDisabled(true);
        }

        // native prompt was dismissed without a choice — close modal and leave toggle OFF
        setIsPrePromptModalOpen(false);
        return;
      }
      setIsPrePromptModalOpen(false);
      mutate(buildWebPushSubscriptionPayload(newSubscription_0, {
        accessToken,
        personId: authenticatedPersonId
      }));
    } catch {
      // push setup failed (e.g. service worker unavailable) — close modal, leave toggle OFF
      setIsToggleChecked(false);
      setIsPrePromptModalOpen(false);
    } finally {
      setIsHandlingToggle(false);
    }
  };
  const handlePrePromptClose = () => {
    setIsPrePromptModalOpen(false);
    setIsHandlingToggle(false);
  };
  return {
    handlePrePromptAllow,
    handlePrePromptClose,
    handlePushToggle,
    isPrePromptLoading: isHandlingToggle,
    isPrePromptModalOpen,
    isToggleChecked,
    isToggleDisabled: isToggleDisabled || isPostPending || isDeletePending || isHandlingToggle,
    isToggleLoading: isPostPending || isDeletePending || isHandlingToggle
  };
};